<!-- @component Code sample displayed within prose content as a block-level element. -->

<script lang="ts" context="module">
  import {
    transformerNotationDiff,
    transformerNotationHighlight,
    transformerNotationWordHighlight,
  } from "@shikijs/transformers";
  import { createHighlighter } from "shiki";

  // We map language aliases to support common values in Markdown fenced code blocks.
  export const languages = {
    bash: "bash",
    cpp: "cpp",
    dockerfile: "dockerfile",
    go: "go",
    ini: "ini",
    javascript: "javascript",
    js: "javascript",
    json: "json",
    plaintext: "plaintext",
    python: "python",
    py: "python",
    rust: "rust",
    sh: "shell",
    shell: "shell",
    sql: "sql",
    text: "plaintext",
    toml: "toml",
    typescript: "typescript",
    ts: "typescript",
    yaml: "yaml",
  };

  const transformers = [
    transformerNotationHighlight(),
    transformerNotationDiff(),
    transformerNotationWordHighlight(),
  ];

  const highlighter = await createHighlighter({
    themes: ["dark-plus"],
    langs: [...new Set([...Object.values(languages)])],
  });

  type Variant = "minimal" | "default";
</script>

<script lang="ts">
  import { page } from "$app/stores";

  import CopyButton from "./CopyButton.svelte";

  // The point of an array `source` is if you have multiple shell commands
  // This lets us show a prompt for each one of them

  export let source: string | string[] = "";
  export let lang: keyof typeof languages = "text";
  export let symbol: string = "";
  export let copyable: boolean = true;
  export let variant: Variant = "default";

  $: snippets = typeof source == "string" ? [source] : source;
  $: merged = typeof source == "string" ? source : source.join("\n") + "\n";
</script>

<div class="relative">
  <div class="code scroller {variant}">
    {#each snippets as snippet}
      <div class="relative flex min-w-0">
        {#if symbol}
          <div class="symbol" role="presentation">
            {symbol}
          </div>
        {/if}
        {@html highlighter.codeToHtml(snippet.trimEnd(), {
          lang: languages[lang] ?? "plaintext",
          theme: "dark-plus",
          transformers: transformers,
        })}
      </div>
    {/each}
    {#if copyable}
      <div
        class="copy-button absolute {variant === 'minimal'
          ? 'top-0 right-0'
          : 'top-2 right-2  transition-opacity opacity-0'}"
      >
        <CopyButton
          page={$page.url.pathname}
          source={merged}
          variant={variant === "minimal" ? "minimal-code-sample" : "default"}
          analyticsEventName="Code Copied"
        />
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">
  .code {
    @apply rounded border border-c-gray-10 text-sm font-mono bg-white/5 w-full min-w-0 py-3.5;
    overflow-x: auto;
    overflow-x: overlay;
  }

  .code.minimal {
    @apply py-1 text-xs;
  }

  .code :global(.shiki:not(.has-highlighted):not(.has-diff)) {
    @apply !bg-transparent overflow-visible w-full;
  }

  .code :global(.shiki) {
    @apply !bg-transparent overflow-visible w-full px-3.5;
    min-width: min-content;

    :global(.line.diff.add) {
      @apply bg-c-green-100/10 min-w-0 w-full inline-block;
    }
    :global(.line.diff.remove) {
      @apply bg-red-600/10 min-w-0 w-full inline-block;
    }
    :global(.line.diff.add::before) {
      @apply absolute left-1 text-c-green-100;
      content: "+";
    }
    :global(.line.diff.remove::before) {
      @apply absolute left-1 text-c-red-100;
      content: "-";
    }
    :global(.line.highlighted) {
      @apply bg-c-gray-10 min-w-0 w-full inline-block;
    }
    :global(.highlighted-word) {
      @apply bg-c-gray-10 border border-c-gray-20 rounded;
    }
  }

  .code:hover > .copy-button {
    @apply opacity-100;
  }

  .scroller {
    @apply flex flex-col;
    -ms-overflow-style: none; /* IE and Edge */
    overflow-x: auto;
    overflow-x: overlay;
    scrollbar-color: transparent transparent;
  }

  .scroller:hover {
    scrollbar-color: rgb(90, 90, 90) rgba(0, 0, 0, 0); /* Firefox */
  }

  .scroller::-webkit-scrollbar {
    /* For Webkit-based browsers (Chrome, Safari and Opera) */
    display: block;
    height: 8px;
  }

  .scroller::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 10px;
  }

  .scroller:hover::-webkit-scrollbar-thumb {
    background: rgb(90, 90, 90);
  }

  .scroller::-webkit-scrollbar-track {
    border-radius: 10px;
    background: transparent;
  }

  .symbol {
    @apply pl-3.5 text-c-green-100 font-medium select-none;
  }

  /* Safari 11+ */
  @media not all and (min-resolution: 0.001dpcm) {
    @supports (-webkit-appearance: none) and (stroke-color: transparent) {
      .code {
        overflow: auto;
      }
    }
  }
</style>
